// @core/stores/userprofile/UserprofileStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { userprofileApi } from '@core/api';
import type {
  ActivateUserprofileRequest,
  BanUserprofileRequest,
  CreateUserprofileRequest,
  GetUserprofileDefaultIDResponse,
  ListUserprofileBanHistoryResponse,
  ListUserprofilesByIDsRequest,
  ListUserprofilesResponse,
  SearchUserprofilesResponse,
  SetUserprofileDefaultRequest,
  UnbanUserprofileRequest,
  UpdateUserprofileRequest,
  Userprofile,
} from '@core/types/userprofile';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class UserprofileStore {
  // List state
  items: Userprofile[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Userprofile | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Userprofile>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Userprofile | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Userprofile | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Activate user profile
   */
  async ActivateUserprofile(profile_id: string, data: ActivateUserprofileRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await userprofileApi.ActivateUserprofile(profile_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Ban user profile
   */
  async BanUserprofile(profile_id: string, data: BanUserprofileRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await userprofileApi.BanUserprofile(profile_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create a user default profile
   */
  async CreateDefaultProfileOnUserCreated(data: CreateUserprofileRequest): Promise<Userprofile> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await userprofileApi.CreateDefaultProfileOnUserCreated(data);
      const item = response.userprofile;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Create a new user profile
   */
  async CreateUserprofile(data: CreateUserprofileRequest): Promise<Userprofile> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await userprofileApi.CreateUserprofile(data);
      const item = response.userprofile;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Delete user profile
   */
  async DeleteUserprofile(profile_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await userprofileApi.DeleteUserprofile(profile_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== profile_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === profile_id) {
          this.currentItem = null;
        }

        this.removeFromCache(profile_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Delete user profile by user ID
   */
  async DeleteUserprofileByUserID(user_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await userprofileApi.DeleteUserprofileByUserID(user_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== user_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === user_id) {
          this.currentItem = null;
        }

        this.removeFromCache(user_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Get user profile by ID (respects privacy settings)
   */
  async GetUserprofile(profile_id: string): Promise<Userprofile> {
    // Check cache first
    const cached = this.cache.get(profile_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await userprofileApi.GetUserprofile(profile_id);
      const item = response.userprofile;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Get active profile status
   */
  async GetUserprofileActive(profile_id: string): Promise<Userprofile> {
    // Check cache first
    const cached = this.cache.get(profile_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await userprofileApi.GetUserprofileActive(profile_id);
      const item = response.userprofile;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Get user profile (admin)
   */
  async GetUserprofileAdmin(profile_id: string): Promise<Userprofile> {
    // Check cache first
    const cached = this.cache.get(profile_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await userprofileApi.GetUserprofileAdmin(profile_id);
      const item = response.userprofile;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Get default profile for user
   */
  async GetUserprofileDefault(user_id: string): Promise<Userprofile> {
    // Check cache first
    const cached = this.cache.get(user_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await userprofileApi.GetUserprofileDefault(user_id);
      const item = response.userprofile;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Get default profile ID for user
   */
  async GetUserprofileDefaultID(user_id: string): Promise<GetUserprofileDefaultIDResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await userprofileApi.GetUserprofileDefaultID(user_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List ban history for profile
   */
  async ListUserprofileBanHistory(profile_id: string, params?: PaginatedRequest): Promise<ListUserprofileBanHistoryResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await userprofileApi.ListUserprofileBanHistory(profile_id, params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List user profiles with filters (respects privacy settings)
   */
  async ListUserprofiles(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await userprofileApi.ListUserprofiles(params);

      runInAction(() => {
        this.items = response.userprofiles?.profiles ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List all user profiles (admin)
   */
  async ListUserprofilesAdmin(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await userprofileApi.ListUserprofilesAdmin(params);

      runInAction(() => {
        this.items = response.userprofiles?.profiles ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List user profiles by IDs
   */
  async ListUserprofilesByIDs(data: ListUserprofilesByIDsRequest): Promise<ListUserprofilesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await userprofileApi.ListUserprofilesByIDs(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List profiles for user (service method)
   */
  async ListUserprofilesForService(user_id: string, params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await userprofileApi.ListUserprofilesForService(user_id, params);

      runInAction(() => {
        this.items = response.userprofiles?.profiles ?? [];
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Search user profiles (privacy-aware)
   */
  async SearchUserprofiles(params?: { cursor?: string | number | boolean; direction?: string | number | boolean; limit?: string | number | boolean; q?: string | number | boolean; sort?: string | number | boolean; }): Promise<SearchUserprofilesResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await userprofileApi.SearchUserprofiles(params);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Set default profile for user
   */
  async SetUserprofileDefault(user_id: string, profile_id: string, data: SetUserprofileDefaultRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await userprofileApi.SetUserprofileDefault(user_id, profile_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Unban user profile
   */
  async UnbanUserprofile(profile_id: string, data: UnbanUserprofileRequest): Promise<Response> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await userprofileApi.UnbanUserprofile(profile_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update user profile
   */
  async UpdateUserprofile(profile_id: string, data: UpdateUserprofileRequest): Promise<Userprofile> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await userprofileApi.UpdateUserprofile(profile_id, data);
      const item = response.userprofile;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const userprofileStore = new UserprofileStore();
